// @flow
import React, { createContext, useCallback, useState, useEffect, PropsWithChildren, useContext } from 'react';

const MAX_USERS_FETCH_SAME_REQ = 10;

const JiraUsersContext = createContext();

function JiraUsersProvider(props: PropsWithChildren<unknown>) {
    const [usersCache, setUsersCache] = useState({});
    const [isFetchingUsers, setIsFetchingUsers] = useState(false);

    const getJiraUsers = useCallback(async (userAccountIds) => {
        const params = new URLSearchParams({
            maxResults: userAccountIds.length,
        });
        userAccountIds.forEach((id) => params.append('accountId', id));
        const url = `/rest/api/3/user/bulk?${params.toString()}`;
        const jiraUsersBody = await window.PlatformAdapter.requestJira(url);
        return jiraUsersBody.values;
    }, []);

    useEffect(() => {
        if (isFetchingUsers) {
            return;
        }
        setIsFetchingUsers(true);

        (async () => {
            try {
                const userAccountIds = Object.entries(usersCache).reduce((acc, [key, value]) => {
                    if (acc.length <= MAX_USERS_FETCH_SAME_REQ && !value && key !== 'unknown') {
                        acc.push(key);
                    }
                    return acc;
                }, []);

                if (userAccountIds?.length) {
                    const jiraUsers = await getJiraUsers(userAccountIds);
                    setUsersCache((prev) => ({
                        ...jiraUsers.reduce((acc, curr) => ({ ...acc, [curr.accountId]: curr }), { ...prev }),
                    }));
                }
            } finally {
                setIsFetchingUsers(false);
            }
        })();
    }, [getJiraUsers, isFetchingUsers, setIsFetchingUsers, usersCache]);

    return (
        <JiraUsersContext.Provider value={{ usersCache, setUsersCache }}>{props.children}</JiraUsersContext.Provider>
    );
}

const useJiraUsersProvider = () => useContext(JiraUsersContext);

export { JiraUsersContext, JiraUsersProvider, useJiraUsersProvider };
